import React from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { SERVICES, SERVICES_BG, WE_REMOVE } from "../data/content";

const Icon = ({ name, ...props }) => {
  const C = Icons[name] || Icons.Box;
  return <C {...props} />;
};

export const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="py-24 bg-background">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mb-14">
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">What we do</span>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Full-service cleanouts for every job size
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">
            Whether it's a single item or an entire estate, our team handles the heavy lifting and responsible disposal.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border rounded-sm overflow-hidden border border-border">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              data-testid={`service-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              className="group bg-card p-8 hover:bg-secondary/40 transition-colors duration-300"
            >
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-secondary text-accent group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Icon name={s.icon} className="w-6 h-6" />
              </span>
              <h3 className="mt-5 text-xl font-semibold text-foreground tracking-tight">{s.title}</h3>
              <p className="mt-2 text-muted-foreground">{s.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-12" data-testid="we-remove">
          <h3 className="text-xl font-semibold text-foreground tracking-tight">We Remove</h3>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {WE_REMOVE.map((item, i) => (
              <span
                key={item}
                data-testid={`remove-chip-${i}`}
                className="inline-flex items-center gap-2 rounded-sm border border-border bg-card px-3.5 py-1.5 text-sm text-foreground"
              >
                <Icons.Check className="w-3.5 h-3.5 text-accent" /> {item}
              </span>
            ))}
          </div>
        </div>

        <div
          className="mt-12 relative rounded-sm overflow-hidden border border-border"
          style={{ backgroundImage: `url(${SERVICES_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        >
          <div className="absolute inset-0 bg-primary/85" />
          <div className="relative px-8 py-12 md:px-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                Not sure if we take it? Just ask.
              </h3>
              <p className="mt-2 text-white/80">
                We remove furniture, appliances, yard waste, construction debris, and almost everything in between.
              </p>
            </div>
            <a
              data-testid="services-cta-link"
              href="#quote"
              onClick={(e) => { e.preventDefault(); document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" }); }}
              className="shrink-0 inline-flex items-center gap-2 bg-white text-primary font-semibold px-6 py-3 rounded-sm hover:bg-secondary transition-colors"
            >
              Request a Quote <Icons.ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
